import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
  Key,
  Search,
  Filter,
  ChevronDown,
  ExternalLink,
  Edit,
  Trash2,
  Plus,
  Copy,
  AlertCircle,
  Check
} from 'lucide-react';
import { useNavigate, Link } from 'react-router-dom';
import { DashboardLayout } from '../components/layouts/DashboardLayout';
import { Button } from '../ui/Button';
import { useApiKeys } from '../hooks/useApiKeys';
import { cn } from '../utils/cn';
import { toast } from 'sonner';

export const ApiKeysPage: React.FC = () => {
  const { apiKeys, loading, deleteApiKey } = useApiKeys();
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'connected' | 'disconnected' | 'error'>('all');

  // Filter API keys based on search term and status
  const filteredApiKeys = apiKeys
    .filter(key => {
      // Apply search filter
      const searchLower = searchTerm.toLowerCase();
      const matchesSearch = 
        key.exchange_name.toLowerCase().includes(searchLower) ||
        key.api_key_public.toLowerCase().includes(searchLower);
      
      // Apply status filter
      const matchesStatus = statusFilter === 'all' || key.status === statusFilter;
      
      return matchesSearch && matchesStatus;
    });

  const handleDeleteApiKey = async (apiKeyId: string, exchangeName: string) => {
    if (window.confirm(`Are you sure you want to delete the API key for ${exchangeName}?`)) {
      await deleteApiKey(apiKeyId);
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  // Format date
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  // Get status badge
  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'connected':
        return (
          <span className="px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400 flex items-center">
            <Check className="h-3 w-3 mr-1" />
            Connected
          </span>
        );
      case 'disconnected':
        return (
          <span className="px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300">
            Disconnected
          </span>
        );
      case 'error':
        return (
          <span className="px-2 py-1 text-xs font-medium rounded-full bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400 flex items-center">
            <AlertCircle className="h-3 w-3 mr-1" />
            Error
          </span>
        );
      default:
        return (
          <span className="px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300">
            {status}
          </span>
        );
    }
  };

  return (
    <DashboardLayout>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.3 }}
        className="space-y-6"
      >
              {/* Header */}
              <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                <div>
                  <h1 className="text-2xl font-bold text-gray-900 dark:text-white">API Keys</h1>
                  <p className="text-gray-600 dark:text-gray-400">Manage your exchange API keys</p>
                </div>
                
                <div className="flex flex-col md:flex-row gap-3 w-full md:w-auto">
                  {/* Search */}
                  <div className="relative">
                    <input
                      type="text"
                      placeholder="Search API keys..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full md:w-64 pl-10 pr-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                    <Search className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
                  </div>
                  
                  {/* Status Filter */}
                  <div className="relative">
                    <select
                      value={statusFilter}
                      onChange={(e) => setStatusFilter(e.target.value as any)}
                      className="w-full md:w-40 pl-10 pr-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none"
                    >
                      <option value="all">All Status</option>
                      <option value="connected">Connected</option>
                      <option value="disconnected">Disconnected</option>
                      <option value="error">Error</option>
                    </select>
                    <Filter className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
                    <ChevronDown className="absolute right-3 top-2.5 h-5 w-5 text-gray-400 pointer-events-none" />
                  </div>
                </div>
              </div>

              {/* IP Whitelist Info */}
              <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg space-y-2">
                <div className="flex items-start gap-2">
                  <AlertCircle className="h-5 w-5 text-blue-500 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-blue-700 dark:text-blue-300 text-sm">
                      For security reasons, you need to whitelist these IP addresses in your exchange settings:
                    </p>
                    <p className="text-blue-600 dark:text-blue-400 text-xs">
                      This allows our trading servers to execute trades on your behalf.
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2 bg-white dark:bg-gray-800 p-2 rounded border border-blue-200 dark:border-blue-800">
                  <code className="text-sm text-blue-600 dark:text-blue-400 flex-1 overflow-x-auto">
                    54.194.236.69, 34.247.157.15, 108.129.50.219, 34.255.23.52, 3.251.100.11, 52.214.84.203
                  </code>
                  <button
                    onClick={() => copyToClipboard("54.194.236.69, 34.247.157.15, 108.129.50.219, 34.255.23.52, 3.251.100.11, 52.214.84.203")}
                    className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
                  >
                    <Copy className="h-4 w-4 text-gray-500 dark:text-gray-400" />
                  </button>
                </div>
              </div>

              {/* API Keys List */}
              {loading ? (
                <div className="flex items-center justify-center h-64">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
                </div>
              ) : filteredApiKeys.length > 0 ? (
                <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md border border-gray-200 dark:border-gray-700">
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                      <thead className="bg-gray-50 dark:bg-gray-700">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                            Exchange
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                            API Key
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                            Status
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                            Created
                          </th>
                          <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                        {filteredApiKeys.map((apiKey) => (
                          <tr key={apiKey.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="flex items-center">
                                <div className="h-8 w-8 bg-gray-100 dark:bg-gray-700 rounded-lg flex items-center justify-center">
                                  <Key className="h-5 w-5 text-gray-500 dark:text-gray-400" />
                                </div>
                                <span className="ml-3 text-sm font-medium text-gray-900 dark:text-white">
                                  {apiKey.exchange_name}
                                </span>
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                              <div className="flex items-center">
                                <span className="font-mono">
                                  {apiKey.api_key_public.substring(0, 8)}...{apiKey.api_key_public.substring(apiKey.api_key_public.length - 8)}
                                </span>
                                <button
                                  onClick={() => copyToClipboard(apiKey.api_key_public)}
                                  className="ml-2 p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
                                >
                                  <Copy className="h-4 w-4 text-gray-500 dark:text-gray-400" />
                                </button>
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              {getStatusBadge(apiKey.status)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                              {formatDate(apiKey.created_at)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-right">
                              <div className="flex justify-end space-x-2">
                                <button 
                                  onClick={() => handleDeleteApiKey(apiKey.id, apiKey.exchange_name)}
                                  className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ) : (
                <div className="bg-white dark:bg-gray-800 rounded-xl p-8 text-center shadow-md border border-gray-200 dark:border-gray-700">
                  <Key className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">No API keys found</h3>
                  <p className="text-gray-600 dark:text-gray-400 mb-6">
                    {searchTerm || statusFilter !== 'all' 
                      ? 'Try adjusting your search or filter criteria'
                      : 'Add your first API key to connect to an exchange'}
                  </p>
                  <div className="text-sm text-gray-600 dark:text-gray-400 mt-4">
                    <p>API keys can only be added through the bot creation process.</p>
                    <p className="mt-2">Go to <Link to="/create-bot" className="text-blue-500 hover:underline">Create Bot</Link> → Exchange Settings → Connect to Exchange</p>
                  </div>
                </div>
              )}
      </motion.div>
    </DashboardLayout>
  );
};

export default ApiKeysPage;